<template>
    <rkm-modal
        title="Novo paciente"
        ref="Modal"
        extraLarge
    >
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-area
                title="Paciente"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-input-autocomplete
                            label="Nome"
                            v-model="model.paciente"
                            :repository="repositories.paciente"
                            :search-fields="autocomplete.paciente"
                            :text-field="(item) =>  item.cidadao.cns ? `${this.formatCns(item.cidadao.cns)} - ${item.cidadao.nome}` : item.cidadao.nome"
                            :validations="validations.paciente"
                            :disabled="model.exportacao"
                            uppercase
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            label="Nome social"
                            v-model="nomeSocial"
                            disabled
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            label="CNS"
                            v-model="cns"
                            disabled
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            label="Data de nascimento"
                            v-model="dataNascimento"
                            disabled
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            label="Área/Microárea"
                            v-model="area"
                            disabled
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            label="Local de atendimento"
                            :options="form.locaisAtendimento"
                            v-model="model.localAtendimentoId"
                            :validations="validations.localAtendimento"
                            value-field="id"
                            text-field="descricao"
                            show-default-option
                            :disabled="model.exportacao"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            label="Tipo de atendimento"
                            :options="form.tiposAtendimento"
                            v-model="model.tipoAtendimentoId"
                            :validations="validations.tipoAtendimento"
                            value-field="id"
                            text-field="descricao"
                            show-default-option
                            :disabled="model.exportacao"
                            @change="handleTipoAtendimento()"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            label="Modalidade"
                            :options="form.modalidades"
                            v-model="model.modalidadeId"
                            :validations="validations.modalidade"
                            value-field="id"
                            text-field="descricao"
                            show-default-option
                            :disabled="disableModalidade"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            label="Turno"
                            :options="form.turnos"
                            v-model="model.turnoId"
                            :validations="validations.turno"
                            value-field="id"
                            text-field="nome"
                            show-default-option
                            :disabled="model.exportacao"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                title="Condições avaliadas"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-checkbox-group
                            v-model="model.condicoes"
                            :disabled="disableCondicoesAvaliadas"
                            :validations="{}"
                        >
                            <rkm-form-checkbox
                                v-for="condicao in form.condicoes"
                                :key="condicao.codigo"
                                :value="condicao.codigo"
                                class="w-50 m-0"
                            >
                                {{ condicao.nome }}
                            </rkm-form-checkbox>
                        </rkm-form-checkbox-group>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                v-if="model.paciente"
                type="fieldset"
                title="Problemas e/ou condições avaliados *"
            >
                <FormProblemaCondicao
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                v-if="model.paciente && model.paciente.cidadao.idade.anos >= 60"
                title="IVCF - Índice de vulnerabilidade clínico-funcional"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col>
                        <p>
                            As perguntas são direcionadas à pessoa idosa e, quando possível, devem ser confirmadas pelo familiar ou acompanhante, desde
                            que convivam com a pessoa idosa e estejam em condições de responder aos questionamentos. Nas pessoas idosas incapazes de
                            responder, utilizar as respostas do cuidador.
                        </p>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-alert>Em geral o tempo de aplicação deste instrumento varia de 5 a 10 minutos.</rkm-alert>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-radio-group
                            @change="handleDesejaPreencherChange"
                            label="Deseja preencher o IVCF 20 para esta pessoa?"
                            v-model="model.ivcf.desejaPreencher"
                        >
                            <rkm-form-radio :value="true">Sim</rkm-form-radio>
                            <rkm-form-radio :value="false">Nao</rkm-form-radio>
                        </rkm-form-radio-group>
                    </rkm-col>
                </rkm-row>

                <template v-if="model.ivcf.desejaPreencher">
                    <rkm-row>
                        <rkm-col>
                            <h5>Perguntas do instrumento</h5>
                        </rkm-col>
                    </rkm-row>

                    <rkm-area title="Idade do cidadão | Grupo 1 de 8">
                        <rkm-form-input
                            :value="`${model.paciente.cidadao.idade.anos} anos e ${model.paciente.cidadao.idade.meses} meses.`"
                            name="pacienteIdade"
                            readonly
                        />
                    </rkm-area>

                    <rkm-area no-margin-top title="Percepção da saúde | Grupo 2 de 8">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.estadoSaudeGeral"
                                    label="Em geral, comparando com outras pessoas de sua idade, você diria que sua saúde é"
                                    v-model="model.ivcf.estadoSaudeGeral"
                                >
                                    <rkm-form-radio value="excelente_muito_boa_ou_boa">Excelente, muito boa ou boa</rkm-form-radio>
                                    <rkm-form-radio value="regular_ou_ruim">Regular ou ruim</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Atividades de vida diária | Grupo 3 de 8">
                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Atividades de vida diária instrumental</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <p>Por causa de sua saúde ou condição física,</p>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoCompras"
                                    label="Você deixou de fazer compras?"
                                    v-model="model.ivcf.limitacaoCompras"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não ou não faz compras por outros motivos que não a saúde</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoGestaoFinanceira"
                                    label="Você deixou de controlar seu dinheiro, gastos ou pagar as contas de sua casa?"
                                    v-model="model.ivcf.limitacaoGestaoFinanceira"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não ou não controla o dinheiro por outros motivos que não a saúde</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoTarefasDomesticas"
                                    label="Você deixou de realizar pequenos trabalhos domésticos, como lavar a louça, arrumar a casa ou fazer limpeza leve?"
                                    v-model="model.ivcf.limitacaoTarefasDomesticas"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não ou não faz mais pequenos trabalhos domésticos por outros motivos que não a saúde</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Atividades de vida diária básica</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.banhoIndependencia"
                                    label="Por causa de sua saúde ou condição física, você deixou de tomar banho sozinho?"
                                    v-model="model.ivcf.banhoIndependencia"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Cognição | Grupo 4 de 8">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.relatoEsquecimentoPorTerceiros"
                                    label="Algum familiar ou amigo falou que você está ficando esquecido?"
                                    v-model="model.ivcf.relatoEsquecimentoPorTerceiros"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.esquecimentoPiorando"
                                    label="Você tem um esquecimento que está piorando nos últimos meses?"
                                    v-model="model.ivcf.esquecimentoPiorando"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.esquecimentoImpactaAtividades"
                                    label="Você tem um esquecimento que está impedindo a realização de alguma atividade do cotidiano?"
                                    v-model="model.ivcf.esquecimentoImpactaAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Humor | Grupo 5 de 8">
                        <rkm-row>
                            <rkm-col>
                                <p>No último mês,</p>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.humorTristezaDesanimo"
                                    label="Você ficou com desânimo, tristeza ou desesperança?"
                                    v-model="model.ivcf.humorTristezaDesanimo"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.perdaInteresseAtividades"
                                    label="Você perdeu o interesse ou prazer em atividades anteriormente prazerosas?"
                                    v-model="model.ivcf.perdaInteresseAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Mobilidade | Grupo 6 de 8">
                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Alcance, preensão e pinça</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoElevarBracos"
                                    label="Você é incapaz de elevar os braços acima do nível do ombro?"
                                    v-model="model.ivcf.limitacaoElevarBracos"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoMotricidadeFina"
                                    label="Você é incapaz de manusear ou segurar pequenos objetos?"
                                    v-model="model.ivcf.limitacaoMotricidadeFina"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Capacidade aeróbica e/ou muscular</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.perdaPesoNaoIntencional"
                                    label="Você teve perda de peso não intencional de 4,5 kg ou 5% do peso corporal no último ano, 6 kg nos últimos 6 meses ou 3 kg no último mês?"
                                    v-model="model.ivcf.perdaPesoNaoIntencional"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.imcAbaixo22"
                                    label="Você tem Índice de Massa Corporal (IMC) menor que 22 kg/m²?"
                                    v-model="model.ivcf.imcAbaixo22"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.panturrilhaAbaixo31"
                                    label="Você tem a circunferência (perímetro) da panturrilha menor que 31 centímetros?"
                                    v-model="model.ivcf.panturrilhaAbaixo31"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.marchaLenta4mMaior5s"
                                    label="Em um teste de velocidade da marcha, o tempo gasto pela pessoa idosa para percorrer 4 metros é maior do que 5 segundos?"
                                    v-model="model.ivcf.marchaLenta4mMaior5s"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Marcha</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.dificuldadeCaminharImpactoAtividades"
                                    label="Você tem dificuldade para caminhar que possa impedir a realização de alguma atividade do cotidiano?"
                                    v-model="model.ivcf.dificuldadeCaminharImpactoAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.quedasRecorrentes"
                                    label="Você teve duas ou mais quedas no último ano?"
                                    v-model="model.ivcf.quedasRecorrentes"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Continência esfincteriana</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.incontinencia"
                                    label="Você perde urina ou fezes, sem querer, em algum momento?"
                                    v-model="model.ivcf.incontinencia"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Comunicação | Grupo 7 de 8">
                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Visão</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.problemaVisaoImpactoAtividades"
                                    label="Você tem problemas de visão capazes de impedir a realização de alguma atividade do cotidiano? (É permitido o uso de óculos ou lentes de contato)"
                                    v-model="model.ivcf.problemaVisaoImpactoAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Audição</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.problemaAudicaoImpactoAtividades"
                                    label="Você tem problemas de audição capazes de impedir a realização de alguma atividade do cotidiano? (É permitido o uso de aparelhos de audição)"
                                    v-model="model.ivcf.problemaAudicaoImpactoAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Comorbidade múltipla | Grupo 8 de 8">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.doencasCronicasMultiplas"
                                    label="Você tem cinco ou mais doenças crônicas?"
                                    v-model="model.ivcf.doencasCronicasMultiplas"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.polifarmacia5OuMaisMedicamentos"
                                    label="Você faz uso regular de cinco ou mais medicamentos diferentes todos os dias?"
                                    v-model="model.ivcf.polifarmacia5OuMaisMedicamentos"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.internacaoUltimos6Meses"
                                    label="Você teve alguma internação nos últimos 6 meses?"
                                    v-model="model.ivcf.internacaoUltimos6Meses"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <template v-if="pontuacaoIvcf > 0">
                        <rkm-area no-margin-top title="Pontuação final do IVCF 20">
                            <rkm-alert
                                v-if="pontuacaoIvcf <= 6"
                                color="success"
                            >
                                Idoso com BAIXO risco de vulnerabilidade Clínico-funcional – IDOSO ROBUSTO
                            </rkm-alert>

                            <rkm-alert
                                v-if="pontuacaoIvcf >= 7 && pontuacaoIvcf <= 14"
                                color="warning"
                            >
                                Idoso com MODERADO risco de vulnerabilidade Clínico-funcional – IDOSO EM RISCO DE FRAGILIZAÇÃO
                            </rkm-alert>

                            <rkm-alert
                                v-if="pontuacaoIvcf >= 15"
                                color="danger"
                            >
                                Idoso com ALTO risco de vulnerabilidade Clínico-funcional – IDOSO FRÁGIL
                            </rkm-alert>
                        </rkm-area>
                    </template>
                </template>
            </rkm-area>

            <rkm-area
                title="Procedimentos"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-checkbox-group
                            v-model="model.procedimentos"
                            :disabled="disableProcedimentos"
                        >
                            <rkm-form-checkbox
                                v-for="procedimento in form.procedimentos"
                                :key="procedimento.codigo"
                                :value="procedimento"
                                class="w-50 m-0"
                            >
                                {{ procedimento.nome }}
                            </rkm-form-checkbox>
                        </rkm-form-checkbox-group>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                title="Outros procedimentos"
            >
                <rkm-row>
                    <rkm-col cols="11">
                        <rkm-form-input-autocomplete
                            name="Outros procedimentos"
                            v-model="model.procedimento"
                            :repository="repositories.procedimento"
                            :request-query-string-params="autocompleteRequestParams.procedimento.queryString"
                            :search-fields="fields.autocomplete.procedimento"
                            :text-field="p => `${this.formatProcedimento(p.codigo)} - ${p.nome}`"
                            :disabled="disableProcedimentos"
                        />
                    </rkm-col>

                    <rkm-col cols="1">
                        <rkm-button
                            @click="incluirProcedimento()"
                            :disabled="disableProcedimentos"
                            color="primary"
                        >Incluir</rkm-button>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-table
                            hide-filters
                            hide-pagination
                            no-footer-sorting
                            :items="procedimentos"
                            :fields="tableFields"
                        >
                            <template v-slot:cell(actions)="{ index }">
                                <rkm-button
                                    @click="handleDestroy(index)"
                                    color="danger"
                                    title="Excluir"
                                    :disabled="model.exportacao"
                                    small
                                >
                                    <rkm-icon icon="trash"/>
                                </rkm-button>
                            </template>
                        </rkm-table>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                title="Outros"
            >
                <rkm-form-select
                    label="Conduta/Desfecho"
                    :options="form.condutaDesfecho"
                    v-model="model.condutaDesfechoId"
                    :validations="validations.condutaDesfecho"
                    value-field="id"
                    text-field="descricao"
                    show-default-option
                    :disabled="model.exportacao"
                />
            </rkm-area>
        </rkm-form>

        <template v-slot:footer="{ cancel }">
            <rkm-button @click="cancel"> Fechar </rkm-button>

            <rkm-button
                color="primary"
                @click="savePaciente"
                :disabled="model.exportacao"
            >
                Adicionar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
import tipoAtendimento from '@/enums/atencao-basica/atendimento-domiciliar/tipoAtendimento';
import procedimento from '@/enums/atencao-basica/atendimento-domiciliar/procedimento';
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import FormProblemaCondicao from './problema-condicao/Index.vue'
import { repository, error, formats, stringCase }  from '@/mixins';
import sexo from '@/enums/cadastro/cidadao/sexo'
import { mapActions } from "vuex";

export default {
    components: {
        FormProblemaCondicao,
    },
    created() {
        this.repositories = {
            atendimentoDomiciliar: this.getConectaSusRepository('atencaoBasica.atendimentoDomiciliar'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            ciap: this.getConectaSusRepository('geral.ciap'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        index: null,
        atendimentoId: null,
        enums: {
            sexo,
            tipoAtendimento,
            procedimento,
            subclassificacao
        },
    }),
    computed: {
        procedimentos() {
            return this.model.procedimentos.filter(procedimento => {
                return ![
                    this.enums.procedimento.VISITA_DOMICILIAR_POS_OBITO,
                    this.enums.procedimento.COLETA_MATERIAL_EXAME_LABORATORIAL,
                    this.enums.procedimento.ANTIBIOTICOTERAPIA_PARENTERAL,
                    this.enums.procedimento.ATENDIMENTO_MEDICO_FINALIDADE_ATESTAR_OBITO,
                    this.enums.procedimento.TERAPIA_REIDRATACAO_PARENTERAL,
                    this.enums.procedimento.ACOMPANHAMENTO_PACIENTE_REABILITACAO_COMUNICACAO_ALTERNATIVA,
                    this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_REABILITACAO_MULTIPLAS_DEFICIENCIAS,
                    this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_PACIENTE_REABILITACAO_DESENVOLVIMENTO_NEUROPSICOMOTOR,
                    this.enums.procedimento.TERAPIA_FONOAUDIOLOGICA_INDIVIDUAL,
                    this.enums.procedimento.CATETERISMO_VESICAL_ALIVIO,
                    this.enums.procedimento.CATETERISMO_VESICAL_DEMORA,
                    this.enums.procedimento.CUIDADOS_ESTOMAS,
                    this.enums.procedimento.CUIDADOS_TRAQUEOSTOMIA,
                    this.enums.procedimento.ENEMA,
                    this.enums.procedimento.OXIGENOTERAPIA_DIA,
                    this.enums.procedimento.RETIRADA_PONTOS_CIRURGIAS,
                    this.enums.procedimento.SONDAGEM_GASTRICA,
                    this.enums.procedimento.TERAPIA_DE_REIDRATACAO_ORAL,
                    this.enums.procedimento.ATENDIMENTO_FISIOTERAPEUTICO_PACIENTE_TRANSTORNO_RESPIRATORIO_SEM_COMPLICACOES_SISTEMICAS,
                    this.enums.procedimento.TRATAMENTO_EM_REABILITACAO,
                    this.enums.procedimento.TRATAMENTO_DE_TRAUMATISMOS_DE_LOCALIZACAO_ESPECIFICADA_NAO_ESPECIFICADA,
                    this.enums.procedimento.TRAQUEOSTOMIA,
                ].includes(procedimento.codigo)
            })
        },
        disableModalidade() {
            return this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO || this.model.exportacao
        },
        disableCondicoesAvaliadas() {
            return this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO || this.model.exportacao
        },
        disableProcedimentos() {
            return this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO || this.model.procedimentos.length >= 30 || this.model.exportacao
        },
        autocompleteRequestParams() {

            const ocupacao = this.form.ocupacoes.find(u => u.id === this.model.ocupacaoId) || {};

            return {
                procedimento: {
                    queryString: {
                        search_by_apelido: this.model.searchByApelido,
                        paciente_id: this.model.paciente
                            ? this.model.paciente.id
                            : null,
                        cbo: ocupacao.codigo
                            ? ocupacao.codigo
                            : null,
                        only_with_cbo: true,
                        atendimento_domiciliar: true,
                        where_not: {
                            'codigo': [
                                this.enums.procedimento.VISITA_DOMICILIAR_POS_OBITO,
                                this.enums.procedimento.COLETA_MATERIAL_EXAME_LABORATORIAL,
                                this.enums.procedimento.ANTIBIOTICOTERAPIA_PARENTERAL,
                                this.enums.procedimento.ATENDIMENTO_MEDICO_FINALIDADE_ATESTAR_OBITO,
                                this.enums.procedimento.TERAPIA_REIDRATACAO_PARENTERAL,
                                this.enums.procedimento.ACOMPANHAMENTO_PACIENTE_REABILITACAO_COMUNICACAO_ALTERNATIVA,
                                this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_REABILITACAO_MULTIPLAS_DEFICIENCIAS,
                                this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_PACIENTE_REABILITACAO_DESENVOLVIMENTO_NEUROPSICOMOTOR,
                                this.enums.procedimento.TERAPIA_FONOAUDIOLOGICA_INDIVIDUAL,
                                this.enums.procedimento.CATETERISMO_VESICAL_ALIVIO,
                                this.enums.procedimento.CATETERISMO_VESICAL_DEMORA,
                                this.enums.procedimento.CUIDADOS_ESTOMAS,
                                this.enums.procedimento.CUIDADOS_TRAQUEOSTOMIA,
                                this.enums.procedimento.ENEMA,
                                this.enums.procedimento.OXIGENOTERAPIA_DIA,
                                this.enums.procedimento.RETIRADA_PONTOS_CIRURGIAS,
                                this.enums.procedimento.SONDAGEM_GASTRICA,
                                this.enums.procedimento.TERAPIA_DE_REIDRATACAO_ORAL,
                                this.enums.procedimento.ATENDIMENTO_FISIOTERAPEUTICO_PACIENTE_TRANSTORNO_RESPIRATORIO_SEM_COMPLICACOES_SISTEMICAS,
                                this.enums.procedimento.TRATAMENTO_EM_REABILITACAO,
                                this.enums.procedimento.TRATAMENTO_DE_TRAUMATISMOS_DE_LOCALIZACAO_ESPECIFICADA_NAO_ESPECIFICADA,
                                this.enums.procedimento.TRAQUEOSTOMIA,
                            ]
                        }
                    }
                },
                cid: {
                    sexo: this.model.paciente?.cidadao?.sexoId
                    ? this.model.paciente.cidadao.sexoId == this.enums.sexo.MASCULINO ? 'M' : 'F'
                    : null,
                },
                ciap: {
                    sexo_id: this.model.paciente?.cidadao?.sexoId,
                },
            }
        },
        fields() {
            return {
                autocomplete: {
                    procedimento: [
                        { key: "nome", searchable: true },
                        { key: "codigo", searchable: true },
                    ],
                    cid: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    ciap: [
                        { key: 'codigo', searchable: true },
                        { key: 'descricao', searchable: true },
                    ],
                }
            }
        },
        tableFields() {
            return [
                {
                    key: "nome",
                    label: "Procedimento",
                    fixed: true
                },
                {
                    name: 'actions',
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min'
                },
            ]
        },
        validations() {
            return {
                paciente: {
                    required: true
                },
                localAtendimento: {
                    required: true
                },
                tipoAtendimento: {
                    required: true
                },
                modalidade: {
                    required: true
                },
                turno: {
                    required: true
                },
                condutaDesfecho: {
                    required: true
                },
                ivcf: {
                    estadoSaudeGeral: { required: true },
                    limitacaoCompras: { required: true },
                    limitacaoGestaoFinanceira: { required: true },
                    limitacaoTarefasDomesticas: { required: true },
                    banhoIndependencia: { required: true },
                    relatoEsquecimentoPorTerceiros: { required: true },
                    esquecimentoPiorando: { required: true },
                    esquecimentoImpactaAtividades: { required: true },
                    humorTristezaDesanimo: { required: true },
                    perdaInteresseAtividades: { required: true },
                    limitacaoElevarBracos: { required: true },
                    limitacaoMotricidadeFina: { required: true },
                    perdaPesoNaoIntencional: { required: true },
                    imcAbaixo22: { required: true },
                    panturrilhaAbaixo31: { required: true },
                    marchaLenta4mMaior5s: { required: true },
                    dificuldadeCaminharImpactoAtividades: { required: true },
                    quedasRecorrentes: { required: true },
                    incontinencia: { required: true },
                    problemaVisaoImpactoAtividades: { required: true },
                    problemaAudicaoImpactoAtividades: { required: true },
                    doencasCronicasMultiplas: { required: true },
                    polifarmacia5OuMaisMedicamentos: { required: true },
                    internacaoUltimos6Meses: { required: true },
                },
            }
        },
        autocomplete() {
            return {
                paciente: [
                    { key: 'cidadao.nome', searchable: true },
                ],
            }
        },
        nomeSocial() {
            return this.model.paciente?.cidadao?.nomeSocial ?? null
        },
        cns() {
            return this.model.paciente?.cidadao?.cns ? this.formatCns(this.model.paciente?.cidadao.cns) : null
        },
        dataNascimento() {
            return this.model.paciente?.cidadao.dataNascimento ? this.formatDate(this.model.paciente?.cidadao.dataNascimento) : null
        },
        area() {
            return this.model.area
        },
        pontuacaoIvcf() {
            let pontuacao = 0

            if (this.model.paciente.cidadao.idade.anos >= 75 && this.model.paciente.cidadao.idade.anos <= 84) {
                pontuacao += 1
            }
            else if (this.model.paciente.cidadao.idade.anos >= 85) {
                pontuacao += 3
            }

            if (this.model.ivcf.estadoSaudeGeral === 'regular_ou_ruim') {
                pontuacao += 1
            }

            if (this.model.ivcf.limitacaoCompras || this.model.ivcf.limitacaoGestaoFinanceira || this.model.ivcf.limitacaoTarefasDomesticas) {
                pontuacao += 4
            }

            if (this.model.ivcf.banhoIndependencia) {
                pontuacao += 6
            }

            if (this.model.ivcf.relatoEsquecimentoPorTerceiros) {
                pontuacao += 1
            }

            if (this.model.ivcf.esquecimentoPiorando) {
                pontuacao += 1
            }

            if (this.model.ivcf.esquecimentoImpactaAtividades) {
                pontuacao += 2
            }

            if (this.model.ivcf.humorTristezaDesanimo) {
                pontuacao += 2
            }

            if (this.model.ivcf.perdaInteresseAtividades) {
                pontuacao += 2
            }

            if (this.model.ivcf.limitacaoElevarBracos) {
                pontuacao += 1
            }

            if (this.model.ivcf.limitacaoMotricidadeFina) {
                pontuacao += 1
            }

            if (this.model.ivcf.perdaPesoNaoIntencional || this.model.ivcf.imcAbaixo22 || this.model.ivcf.panturrilhaAbaixo31 || this.model.ivcf.marchaLenta4mMaior5s) {
                pontuacao += 2
            }

            if (this.model.ivcf.dificuldadeCaminharImpactoAtividades) {
                pontuacao += 2
            }

            if (this.model.ivcf.quedasRecorrentes) {
                pontuacao += 2
            }

            if (this.model.ivcf.incontinencia) {
                pontuacao += 2
            }

            if (this.model.ivcf.problemaVisaoImpactoAtividades) {
                pontuacao += 2
            }

            if (this.model.ivcf.problemaAudicaoImpactoAtividades) {
                pontuacao += 2
            }

            if (this.model.ivcf.doencasCronicasMultiplas || this.model.ivcf.polifarmacia5OuMaisMedicamentos || this.model.ivcf.internacaoUltimos6Meses) {
                pontuacao += 4
            }

            return pontuacao
        },
    },
    methods:{
        ...mapActions(["setLoading", "unsetLoading"]),
        getArea() {
            this.setLoading()
            this.repositories.atendimentoDomiciliar.getArea({
                paciente_id: this.model.paciente.id
            })
                .then(({ data }) => {
                    this.model.area = data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        open(index = null) {
            if(index === null) {
                this.clearForm()
            } else {
                this.index = index
                const atendimento = this.model.atendimentos[index]
                this.atendimentoId = atendimento.id
                this.model.paciente = {
                    id: atendimento.pacienteId ?? atendimento.paciente?.id,
                    cidadao: {
                        nome: atendimento.pacienteNome ?? atendimento.paciente?.cidadao.nome,
                        nomeSocial: atendimento.pacienteNomeSocial ?? atendimento.paciente?.cidadao.nomeSocial,
                        cns: atendimento.cns ?? atendimento.paciente?.cidadao.cns,
                        dataNascimento: atendimento.dataNascimento ?? atendimento.paciente?.cidadao.dataNascimento,
                        idade: {
                            anos: atendimento.pacienteIdadeAnos ?? atendimento.paciente?.cidadao.idade?.anos,
                            meses: atendimento.pacienteIdadeMeses ?? atendimento.paciente?.cidadao.idade?.meses,
                        }
                    }
                }
                this.model.localAtendimentoId = atendimento.localAtendimentoId
                this.model.tipoAtendimentoId = atendimento.tipoAtendimentoId
                this.model.modalidadeId = atendimento.modalidadeId
                this.model.turnoId = atendimento.turnoId
                this.model.condicoes = atendimento.condicoes
                this.model.cid = atendimento.codigoCid
                this.model.ciap = atendimento.codigoCiap
                this.model.procedimentos = atendimento.procedimentos
                this.model.condutaDesfechoId = atendimento.condutaDesfechoId
                this.model.condicaoAvaliada = atendimento.condicaoAvaliada
                this.model.doencaTransmissivel = atendimento.doencaTransmissivel
                this.model.rastreamento = atendimento.rastreamento
                this.model.problemasCondicoes = atendimento.problemasCondicoes

                this.model.ivcf = {
                    desejaPreencher: atendimento.ivcf.desejaPreencher,
                    estadoSaudeGeral: atendimento.ivcf.estadoSaudeGeral,
                    limitacaoCompras: atendimento.ivcf.limitacaoCompras,
                    limitacaoGestaoFinanceira: atendimento.ivcf.limitacaoGestaoFinanceira,
                    limitacaoTarefasDomesticas: atendimento.ivcf.limitacaoTarefasDomesticas,
                    banhoIndependencia: atendimento.ivcf.banhoIndependencia,
                    relatoEsquecimentoPorTerceiros: atendimento.ivcf.relatoEsquecimentoPorTerceiros,
                    esquecimentoPiorando: atendimento.ivcf.esquecimentoPiorando,
                    esquecimentoImpactaAtividades: atendimento.ivcf.esquecimentoImpactaAtividades,
                    humorTristezaDesanimo: atendimento.ivcf.humorTristezaDesanimo,
                    perdaInteresseAtividades: atendimento.ivcf.perdaInteresseAtividades,
                    limitacaoElevarBracos: atendimento.ivcf.limitacaoElevarBracos,
                    limitacaoMotricidadeFina: atendimento.ivcf.limitacaoMotricidadeFina,
                    perdaPesoNaoIntencional: atendimento.ivcf.perdaPesoNaoIntencional,
                    imcAbaixo22: atendimento.ivcf.imcAbaixo22,
                    panturrilhaAbaixo31: atendimento.ivcf.panturrilhaAbaixo31,
                    marchaLenta4mMaior5s: atendimento.ivcf.marchaLenta4mMaior5s,
                    dificuldadeCaminharImpactoAtividades: atendimento.ivcf.dificuldadeCaminharImpactoAtividades,
                    quedasRecorrentes: atendimento.ivcf.quedasRecorrentes,
                    incontinencia: atendimento.ivcf.incontinencia,
                    problemaVisaoImpactoAtividades: atendimento.ivcf.problemaVisaoImpactoAtividades,
                    problemaAudicaoImpactoAtividades: atendimento.ivcf.problemaAudicaoImpactoAtividades,
                    doencasCronicasMultiplas: atendimento.ivcf.doencasCronicasMultiplas,
                    polifarmacia5OuMaisMedicamentos: atendimento.ivcf.polifarmacia5OuMaisMedicamentos,
                    internacaoUltimos6Meses: atendimento.ivcf.internacaoUltimos6Meses,
                }
            }
            this.$refs.Modal.show()
        },
        clearForm() {
            this.index = null
            this.model.paciente = null
            this.model.localAtendimentoId = null
            this.model.tipoAtendimentoId = null
            this.model.modalidadeId = null
            this.model.turnoId = null
            this.model.condicoes = []
            this.model.cid = null
            this.model.ciap = null
            this.model.procedimentos = []
            this.model.condutaDesfechoId = null
            this.model.condicaoAvaliada = []
            this.model.doencaTransmissivel = []
            this.model.rastreamento = []
            this.model.problemasCondicoes = []
        },
        incluirProcedimento() {
            if(this.model.procedimento && !this.model.procedimentos.find(procedimento => (procedimento.codigo == this.model.procedimento.codigo))) {
                this.model.procedimentos.push({
                    id: this.model.procedimento.id,
                    nome: this.model.procedimento.nome,
                    codigo: this.model.procedimento.codigo
                })
                this.model.procedimento = null
            }
        },
        handleDestroy(index) {
            this.model.procedimentos.splice(index, 1)
        },
        savePaciente() {

            this.$refs.Form.validate()
                .then(valid => {
                    if(valid) {

                        if(this.index !== null) {
                            this.model.atendimentos.splice(this.index, 1, {
                                id: this.atendimentoId,
                                pacienteId: this.model.paciente?.id,
                                pacienteNome: this.model.paciente?.cidadao?.nome,
                                pacienteNomeSocial: this.model.paciente?.cidadao?.nomeSocial,
                                cns: this.cns,
                                dataNascimento: this.dataNascimento,
                                localAtendimentoId: this.model.localAtendimentoId,
                                tipoAtendimentoId: this.model.tipoAtendimentoId,
                                modalidadeId: this.model.modalidadeId,
                                turnoId: this.model.turnoId,
                                condicoes: this.model.condicoes,
                                cid: this.model.cid?.codigo,
                                ciap: this.model.ciap?.codigo,
                                procedimentos: this.model.procedimentos,
                                condutaDesfechoId: this.model.condutaDesfechoId,
                                condicaoAvaliada: this.model.condicaoAvaliada,
                                doencaTransmissivel: this.model.doencaTransmissivel,
                                rastreamento: this.model.rastreamento,
                                problemasCondicoes: this.model.problemasCondicoes,
                                ivcf: {
                                    desejaPreencher: this.model.ivcf.desejaPreencher,
                                    estadoSaudeGeral: this.model.ivcf.estadoSaudeGeral,
                                    limitacaoCompras: this.model.ivcf.limitacaoCompras,
                                    limitacaoGestaoFinanceira: this.model.ivcf.limitacaoGestaoFinanceira,
                                    limitacaoTarefasDomesticas: this.model.ivcf.limitacaoTarefasDomesticas,
                                    banhoIndependencia: this.model.ivcf.banhoIndependencia,
                                    relatoEsquecimentoPorTerceiros: this.model.ivcf.relatoEsquecimentoPorTerceiros,
                                    esquecimentoPiorando: this.model.ivcf.esquecimentoPiorando,
                                    esquecimentoImpactaAtividades: this.model.ivcf.esquecimentoImpactaAtividades,
                                    humorTristezaDesanimo: this.model.ivcf.humorTristezaDesanimo,
                                    perdaInteresseAtividades: this.model.ivcf.perdaInteresseAtividades,
                                    limitacaoElevarBracos: this.model.ivcf.limitacaoElevarBracos,
                                    limitacaoMotricidadeFina: this.model.ivcf.limitacaoMotricidadeFina,
                                    perdaPesoNaoIntencional: this.model.ivcf.perdaPesoNaoIntencional,
                                    imcAbaixo22: this.model.ivcf.imcAbaixo22,
                                    panturrilhaAbaixo31: this.model.ivcf.panturrilhaAbaixo31,
                                    marchaLenta4mMaior5s: this.model.ivcf.marchaLenta4mMaior5s,
                                    dificuldadeCaminharImpactoAtividades: this.model.ivcf.dificuldadeCaminharImpactoAtividades,
                                    quedasRecorrentes: this.model.ivcf.quedasRecorrentes,
                                    incontinencia: this.model.ivcf.incontinencia,
                                    problemaVisaoImpactoAtividades: this.model.ivcf.problemaVisaoImpactoAtividades,
                                    problemaAudicaoImpactoAtividades: this.model.ivcf.problemaAudicaoImpactoAtividades,
                                    doencasCronicasMultiplas: this.model.ivcf.doencasCronicasMultiplas,
                                    polifarmacia5OuMaisMedicamentos: this.model.ivcf.polifarmacia5OuMaisMedicamentos,
                                    internacaoUltimos6Meses: this.model.ivcf.internacaoUltimos6Meses,
                                }
                            })
                        }
                        else {
                            this.model.atendimentos.push({
                                id: null,
                                pacienteId: this.model.paciente?.id,
                                pacienteNome: this.model.paciente?.cidadao?.nome,
                                cns: this.cns,
                                dataNascimento: this.dataNascimento,
                                localAtendimentoId: this.model.localAtendimentoId,
                                tipoAtendimentoId: this.model.tipoAtendimentoId,
                                modalidadeId: this.model.modalidadeId,
                                turnoId: this.model.turnoId,
                                condicoes: this.model.condicoes,
                                cid: this.model.cid?.codigo,
                                ciap: this.model.ciap?.codigo,
                                procedimentos: this.model.procedimentos,
                                condutaDesfechoId: this.model.condutaDesfechoId,
                                condicaoAvaliada: this.model.condicaoAvaliada,
                                doencaTransmissivel: this.model.doencaTransmissivel,
                                rastreamento: this.model.rastreamento,
                                problemasCondicoes: this.model.problemasCondicoes,
                                ivcf: {
                                    desejaPreencher: this.model.ivcf.desejaPreencher,
                                    estadoSaudeGeral: this.model.ivcf.estadoSaudeGeral,
                                    limitacaoCompras: this.model.ivcf.limitacaoCompras,
                                    limitacaoGestaoFinanceira: this.model.ivcf.limitacaoGestaoFinanceira,
                                    limitacaoTarefasDomesticas: this.model.ivcf.limitacaoTarefasDomesticas,
                                    banhoIndependencia: this.model.ivcf.banhoIndependencia,
                                    relatoEsquecimentoPorTerceiros: this.model.ivcf.relatoEsquecimentoPorTerceiros,
                                    esquecimentoPiorando: this.model.ivcf.esquecimentoPiorando,
                                    esquecimentoImpactaAtividades: this.model.ivcf.esquecimentoImpactaAtividades,
                                    humorTristezaDesanimo: this.model.ivcf.humorTristezaDesanimo,
                                    perdaInteresseAtividades: this.model.ivcf.perdaInteresseAtividades,
                                    limitacaoElevarBracos: this.model.ivcf.limitacaoElevarBracos,
                                    limitacaoMotricidadeFina: this.model.ivcf.limitacaoMotricidadeFina,
                                    perdaPesoNaoIntencional: this.model.ivcf.perdaPesoNaoIntencional,
                                    imcAbaixo22: this.model.ivcf.imcAbaixo22,
                                    panturrilhaAbaixo31: this.model.ivcf.panturrilhaAbaixo31,
                                    marchaLenta4mMaior5s: this.model.ivcf.marchaLenta4mMaior5s,
                                    dificuldadeCaminharImpactoAtividades: this.model.ivcf.dificuldadeCaminharImpactoAtividades,
                                    quedasRecorrentes: this.model.ivcf.quedasRecorrentes,
                                    incontinencia: this.model.ivcf.incontinencia,
                                    problemaVisaoImpactoAtividades: this.model.ivcf.problemaVisaoImpactoAtividades,
                                    problemaAudicaoImpactoAtividades: this.model.ivcf.problemaAudicaoImpactoAtividades,
                                    doencasCronicasMultiplas: this.model.ivcf.doencasCronicasMultiplas,
                                    polifarmacia5OuMaisMedicamentos: this.model.ivcf.polifarmacia5OuMaisMedicamentos,
                                    internacaoUltimos6Meses: this.model.ivcf.internacaoUltimos6Meses,
                                }
                            })
                        }
                        this.$refs.Modal.hide()
                    }
                })
        },
        handleTipoAtendimento() {

            if (this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO) {

                this.model.condicoes = []
                this.model.procedimentos = []
                this.model.condicaoAvaliada = []
                this.model.doencaTransmissivel = []
                this.model.rastreamento = []
                this.model.problemasCondicoes = []
            }
        },
        handleDesejaPreencherChange(value) {
            if (value === false) {
                this.showConfirmation(
                    'Deseja realmente descartar os dados já preenchidos do IVFC 20?',
                    async () => {
                        this.model.ivcf.estadoSaudeGeral = null
                        this.model.ivcf.limitacaoCompras = null
                        this.model.ivcf.limitacaoGestaoFinanceira = null
                        this.model.ivcf.limitacaoTarefasDomesticas = null
                        this.model.ivcf.banhoIndependencia = null
                        this.model.ivcf.relatoEsquecimentoPorTerceiros = null
                        this.model.ivcf.esquecimentoPiorando = null
                        this.model.ivcf.esquecimentoImpactaAtividades = null
                        this.model.ivcf.humorTristezaDesanimo = null
                        this.model.ivcf.perdaInteresseAtividades = null
                        this.model.ivcf.limitacaoElevarBracos = null
                        this.model.ivcf.limitacaoMotricidadeFina = null
                        this.model.ivcf.perdaPesoNaoIntencional = null
                        this.model.ivcf.imcAbaixo22 = null
                        this.model.ivcf.panturrilhaAbaixo31 = null
                        this.model.ivcf.marchaLenta4mMaior5s = null
                        this.model.ivcf.dificuldadeCaminharImpactoAtividades = null
                        this.model.ivcf.quedasRecorrentes = null
                        this.model.ivcf.incontinencia = null
                        this.model.ivcf.problemaVisaoImpactoAtividades = null
                        this.model.ivcf.problemaAudicaoImpactoAtividades = null
                        this.model.ivcf.doencasCronicasMultiplas = null
                        this.model.ivcf.polifarmacia5OuMaisMedicamentos = null
                        this.model.ivcf.internacaoUltimos6Meses = null
                    },
                    async () => {
                        this.model.ivcf.desejaPreencher = true
                    }
                )
            }
        },
    },
    watch: {
        'model.paciente': function() {

            if(this.model.paciente) {

                this.setLoading()

                this.repositories.paciente.validCNS(this.camelObjectKeysToSnakeRecursive({pacienteId: this.model.paciente.id}))
                    .then(response => {

                        if (!response.data.additionals.cnsValido && response.data.additionals.avisaCnsTemporarioInvalido) {

                            this.showWarningNotification('Paciente com CNS inválido. Registro não permitido.')
                            this.model.paciente = null
                            return false
                        }

                        if (this.model.paciente.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                            && response.data.additionals.avisaCnsTemporarioInvalido) {

                                const confirm = async () => {

                                    this.getArea()
                                    return false
                                }

                                const cancel = async () => {

                                    this.model.paciente = null
                                    return false
                                }

                                const msg = `Paciente com cadastro temporário. Deseja continuar?`

                                this.showConfirmation(msg, confirm, cancel)

                                return false
                        }

                        this.getArea()
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        'model.tipoAtendimentoId': function() {
            if(this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO) {
                this.model.condicoes = []
            }
        }
    },
    props: {
        model: { type: Object, required: true },
        form: { type: Object, required: true }
    },
    mixins: [ repository, error, formats, stringCase ]
}
</script>